import { useEffect, useMemo, useState } from "react";

const CACHE_KEY = "ivision-eur-huf-rate";
const FALLBACK_EUR_TO_HUF = 390;

type RateCache = {
  date: string;
  rate: number;
};

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function readCachedRate(): number | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RateCache;
    if (parsed.date !== todayKey() || !Number.isFinite(parsed.rate)) return null;
    return parsed.rate;
  } catch {
    return null;
  }
}

function cacheRate(rate: number) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ date: todayKey(), rate }));
  } catch {
    // Ignore storage failures; the fallback keeps prices stable.
  }
}

export function roundEurUpTo99(value: number) {
  const cents = Math.round(value * 100);
  const whole = Math.floor(cents / 100);
  const currentCent = cents - whole * 100;
  return currentCent <= 99 ? whole + 0.99 : whole + 1.99;
}

export function convertHufToRoundedEur(huf: number, eurToHufRate: number) {
  return roundEurUpTo99(huf / eurToHufRate);
}

export function formatHuf(huf: number) {
  return `${new Intl.NumberFormat("hu-HU").format(huf)} Ft`;
}

export function formatEur(eur: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(eur);
}

export function useEurRate() {
  const [rate, setRate] = useState(FALLBACK_EUR_TO_HUF);
  const [source, setSource] = useState<"cache" | "api" | "fallback">("fallback");

  useEffect(() => {
    const cached = readCachedRate();
    if (cached) {
      setRate(cached);
      setSource("cache");
      return;
    }

    let cancelled = false;
    fetch("https://api.frankfurter.dev/v1/latest?from=EUR&to=HUF")
      .then((response) => {
        if (!response.ok) throw new Error("Exchange rate request failed");
        return response.json();
      })
      .then((data) => {
        const nextRate = Number(data?.rates?.HUF);
        if (!Number.isFinite(nextRate) || nextRate <= 0) throw new Error("Invalid exchange rate");
        cacheRate(nextRate);
        if (!cancelled) {
          setRate(nextRate);
          setSource("api");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRate(FALLBACK_EUR_TO_HUF);
          setSource("fallback");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ rate, source }), [rate, source]);
}

export function priceForSeo(huf: number, language: "hu" | "en", eurToHufRate = FALLBACK_EUR_TO_HUF) {
  if (language === "hu") return { price: String(huf), currency: "HUF" };
  return { price: convertHufToRoundedEur(huf, eurToHufRate).toFixed(2), currency: "EUR" };
}
